import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  X, Check, PiggyBank, Calendar, Save, AlertCircle, 
  TrendingUp, TrendingDown, Wallet, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const EditPortefeuilleDialog = ({ open, onClose, portefeuille, onSuccess, currentBalance }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    montant_recurrent: '',
    frequence: '',
    jour_prelevement: 1
  });

  useEffect(() => {
    if (portefeuille && open) {
      setFormData({
        nom: portefeuille.nom || '',
        montant_recurrent: portefeuille.montant_recurrent?.toString() || '',
        frequence: portefeuille.frequence || '',
        jour_prelevement: portefeuille.jour_prelevement || 1
      });
    }
  }, [portefeuille, open]);

  // Calculs dynamiques pour montrer l'impact des modifications
  const calculations = useMemo(() => {
    const montant = parseFloat(formData.montant_recurrent) || 0;
    const oldMontant = portefeuille?.montant_recurrent || 0;
    
    let projectionMensuelle = 0;
    let oldProjection = 0;
    
    if (formData.frequence === 'hebdomadaire') {
      projectionMensuelle = montant * 4.33;
      oldProjection = oldMontant * 4.33;
    } else if (formData.frequence === 'mensuel') {
      projectionMensuelle = montant;
      oldProjection = oldMontant;
    }
    
    const difference = projectionMensuelle - oldProjection;
    
    return {
      projectionMensuelle,
      oldProjection,
      difference,
      hasChanged: difference !== 0 || formData.nom !== portefeuille?.nom
    };
  }, [formData, portefeuille]);

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nom: formData.nom.trim(),
        montant_recurrent: portefeuille.type === 'recurrente' ? parseFloat(formData.montant_recurrent) : null,
        frequence: portefeuille.type === 'recurrente' ? formData.frequence : null,
        jour_prelevement: parseInt(formData.jour_prelevement) || 1
      };

      await axios.put(
        `${API}/portefeuilles/${portefeuille.id}`,
        payload,
        { withCredentials: true }
      );

      toast.success('Portefeuille modifiée ! Les changements sont appliqués au calendrier.');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating portefeuille:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !portefeuille) return null;

  const isRecurrent = portefeuille.type === 'recurrente';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Modifier la portefeuille</h2>
                  <p className="text-xs text-white/70">{portefeuille.nom}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
            {/* Current status */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Solde actuel</span>
                <span className="text-xl font-bold text-gray-900">{portefeuille.solde?.toFixed(2) || 0}€</span>
              </div>
              <div className="text-xs text-gray-500">
                {isRecurrent ? (
                  <span className="text-teal-600 font-medium">
                    Portefeuille récurrente • {portefeuille.frequence === 'hebdomadaire' ? 'Chaque semaine' : 'Chaque mois'}
                  </span>
                ) : (
                  'Portefeuille ponctuelle'
                )}
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="nom">Nom de la portefeuille</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: Repas du midi"
                className="mt-1"
              />
            </div>

            {/* Recurring options */}
            {isRecurrent && (
              <>
                <div>
                  <Label htmlFor="montant">Montant {formData.frequence === 'hebdomadaire' ? 'hebdomadaire' : 'mensuel'}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="montant"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.montant_recurrent}
                      onChange={(e) => setFormData(prev => ({ ...prev, montant_recurrent: e.target.value }))}
                      placeholder="0.00"
                      className="text-lg pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">€</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.frequence === 'hebdomadaire' 
                      ? `Soit environ ${calculations.projectionMensuelle.toFixed(0)}€ par mois`
                      : 'Montant prélevé chaque mois'
                    }
                  </p>
                </div>

                {/* Day of month/week */}
                <div>
                  <Label htmlFor="jour">Jour de prélèvement</Label>
                  <Input
                    id="jour"
                    type="number"
                    min="1"
                    max={formData.frequence === 'hebdomadaire' ? 7 : 28}
                    value={formData.jour_prelevement}
                    onChange={(e) => setFormData(prev => ({ ...prev, jour_prelevement: e.target.value }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.frequence === 'hebdomadaire' 
                      ? '1 = Lundi, 7 = Dimanche'
                      : 'Jour du mois (1-28)'
                    }
                  </p>
                </div>

                {/* Impact preview */}
                {calculations.hasChanged && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-amber-50 border border-amber-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="space-y-2 flex-1">
                        <p className="text-sm font-medium text-amber-800">Impact des modifications</p>
                        
                        {calculations.difference !== 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Différence mensuelle</span>
                            <span className={`font-bold ${calculations.difference > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {calculations.difference > 0 ? '+' : ''}{calculations.difference.toFixed(0)}€
                            </span>
                          </div>
                        )}
                        
                        <p className="text-xs text-amber-600">
                          Les opérations futures seront mises à jour automatiquement.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </>
            )}

            {/* Calendar sync info */}
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
              <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-800 font-medium">Synchronisation calendrier</p>
                <p className="text-blue-600 text-xs mt-1">
                  Les modifications seront reflétées dans ton calendrier. Les opérations passées ne sont pas modifiées.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.nom.trim()}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {loading ? (
                  'Modification...'
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditPortefeuilleDialog;
